<template>
  <base-event-log-item
    :icon="icon"
    :icon-color="iconColor"
    :researcher="referenceEventLog.eventBy"
    :date="referenceEventLog.createdAt"
    :bottom-node="bottomNode"
    :color="color"
  >
    <div class="text-overflow-ellipsis">
      <span class="font-weight-medium">
        {{ target }}
      </span>
      <span class="font-weight-light">
        {{ verbs }}
      </span>
      <span class="font-weight-medium">
        {{ outcome }}
      </span>
    </div>
  </base-event-log-item>
</template>

<script>
import BaseEventLogItem from './BaseEventLogItem';
import { ReferenceEventLog, Reference, Researcher } from '@/models';
import { ReferenceEvent, RelevanceAssessment } from '@/enums';
import { startCase } from 'lodash';

const getColor = (outcome) => {
  switch (outcome) {
    case RelevanceAssessment.RELEVANT:
      return 'success';
    case RelevanceAssessment.NOT_RELEVANT:
      return 'error';
    case RelevanceAssessment.NOT_SURE:
      return 'warning';
    default:
      throw new Error('Invalid relevance assessment');
  }
};

const getScreeningIcon = (outcome) => {
  switch (outcome) {
    case RelevanceAssessment.RELEVANT:
      return { name: 'thumb_up_alt', color: getColor(outcome) };
    case RelevanceAssessment.NOT_RELEVANT:
      return { name: 'thumb_down_alt', color: getColor(outcome) };
    default:
      return { name: 'help', color: getColor(outcome) };
  }
};

const SCREENING_VOTE_STYLE_PROPS = {
  target: 'Screening Vote',
  verbs: (eventLog) =>
    eventLog.event === ReferenceEvent.SCREENING_VOTE_CREATE
      ? 'set to'
      : 'changed to',
  icon: ({ payload }, shouldBeBlind) =>
    shouldBeBlind
      ? { name: 'help', color: 'grey darken-2' }
      : getScreeningIcon(payload.screeningVote.vote),
  outcome: ({ payload }, shouldBeBlind) => {
    return shouldBeBlind
      ? '-'
      : startCase(payload.screeningVote.vote.toLowerCase());
  },
};

const SCREENING_OUTCOME_STYLE_PROPS = {
  target: ({ event, payload }) => {
    let target;
    if (payload.screeningOutcome.resolution === 'PRE_SCREENED') {
      target = 'Pre-screened';
    } else {
      target =
        event === ReferenceEvent.SCREENING_OUTCOME_CREATE
          ? 'Screened'
          : 'Screening Outcome';
    }
    return target;
  },
  verbs: ({ event }) =>
    event === ReferenceEvent.SCREENING_OUTCOME_CREATE
      ? 'as'
      : 'changed to',
  icon: ({ payload }, shouldBeBlind) =>
    shouldBeBlind
      ? { name: 'help', color: 'grey darken-2' }
      : getScreeningIcon(payload.screeningOutcome.outcome),
  outcome: ({ payload }, shouldBeBlind) => {
    return shouldBeBlind
      ? '-'
      : startCase(payload.screeningOutcome.outcome.toLowerCase());
  },
  color: ({ payload }, shouldBeBlind) => {
    if (shouldBeBlind) return null;
    return getColor(payload.screeningOutcome.outcome);
  },
};

const EVENT_TO_STYLE_PROPS = {
  [ReferenceEvent.SCREENING_VOTE_CREATE]: SCREENING_VOTE_STYLE_PROPS,
  [ReferenceEvent.SCREENING_VOTE_UPDATE]: SCREENING_VOTE_STYLE_PROPS,
  [ReferenceEvent.SCREENING_VOTE_CONFLICT]: {
    icon: { name: 'error', color: 'error' },
    target: 'Screening Vote Conflict Detected',
    verbs: null,
    outcome: null,
    color: 'error',
  },
  [ReferenceEvent.SCREENING_VOTE_REVOKE]: {
    icon: { name: 'undo', color: 'error' },
    target: 'Screening Vote',
    verbs: 'revoked',
    outcome: null,
    color: 'error',
  },
  [ReferenceEvent.SCREENING_OUTCOME_CREATE]: SCREENING_OUTCOME_STYLE_PROPS,
  [ReferenceEvent.SCREENING_OUTCOME_UPDATE]: SCREENING_OUTCOME_STYLE_PROPS,
  [ReferenceEvent.SCREENING_OUTCOME_REVOKE]: {
    icon: { name: 'undo', color: 'error' },
    target: 'Screening Outcome',
    verbs: 'revoked',
    outcome: null,
    color: 'error',
  },
};

export default {
  name: 'ScreeningEventLogItem',
  components: { BaseEventLogItem },
  props: {
    referenceEventLog: {
      type: ReferenceEventLog,
      required: true,
    },
    reference: {
      type: Reference,
      required: true,
    },
    currentResearcher: {
      type: Researcher,
      required: true,
    },
    bottomNode: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    icon() {
      return this.styleProps.icon.name;
    },
    iconColor() {
      return this.styleProps.icon.color;
    },
    target() {
      return this.styleProps.target;
    },
    verbs() {
      return this.styleProps.verbs;
    },
    outcome() {
      return this.styleProps.outcome;
    },
    color() {
      return this.styleProps.color;
    },
    shouldBeBlind() {
      return (
        !this.reference.isScreened() &&
        this.referenceEventLog.eventBy?.id !==
          this.currentResearcher.id
      );
    },
    styleProps() {
      return Object.entries(
        EVENT_TO_STYLE_PROPS[this.referenceEventLog.event]
      ).reduce((acc, [key, value]) => {
        acc[key] =
          typeof value === 'function'
            ? value(this.referenceEventLog, this.shouldBeBlind)
            : value;
        return acc;
      }, {});
    },
  },
};
</script>

<style scoped>
.text-overflow-ellipsis {
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
